import { DestroyRef, Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';

import { filter } from 'rxjs/operators';
import { ProfileService, UserProfile } from '../profile/profile.service';
import { IconComponent } from '../shared/components/icon/icon.component';
import { CompleteProfilePromptComponent } from '../profile/profile-fields/complete-profile-prompt.component';
import { DueDatesService, DueDateItem, DueDateSummary } from '../services/due-dates.service';
import { HelpIconComponent } from '../shared/components/help-icon/help-icon.component';
import { PermissionService } from '../shared/services/permission.service';
import { InstructorOrganizationService } from '../instructor/instructor-organization.service';
import { CourseService, Course, Certificate } from '../courses/course.service';
import { resolveCourseCompletionStatus } from '../courses/course-list.utils';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, HelpIconComponent, CompleteProfilePromptComponent],
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  currentUser: UserProfile | null = null;
  dueDates: DueDateItem[] = [];
  dueDateSummary: DueDateSummary | null = null;
  isLoadingDueDates = false;
  dueDatesError: string | null = null;

  // Course-related properties
  courses: Course[] = [];
  isLoadingCourses = false;
  coursesError: string | null = null;
  // KAN-411 round 4: the "Your Courses" widget status must gate on genuine
  // completion, same as the My Courses card - a cert-enabled course counts as
  // Completed only once its certificate exists.
  certificates: Certificate[] = [];

  // Role-adaptive dashboard properties
  userRole: string = '';
  organizationContext: any = null;
  isInstructor = false;
  isAdmin = false;

  constructor(
    private profileService: ProfileService,
    private dueDatesService: DueDatesService,
    private permissionService: PermissionService,
    private orgService: InstructorOrganizationService,
    private courseService: CourseService,
    private authService: AuthService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.currentUser = this.profileService.getCurrentUser();
    this.initializeRoleContext();
    this.loadDueDates();
    // Audit finding #2 — the learner "Your Courses" panel is hidden for
    // instructors/admins (they use Instructor Tools > Courses), so skip the
    // GET that hydrates it. Avoids a per-page wasted HTTP for those roles.
    if (!this.isInstructor && !this.isAdmin) {
      this.loadCourses();
    }

    // Subscribe to organization changes via AuthService (same pattern as sidebar)
    // This fires when user profile is updated with new org role
    this.authService.userOrganizations$.pipe(
      filter(orgs => orgs !== null),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      console.log('[Dashboard] Organization changed, reloading user data');
      // Refresh user profile to get updated org context
      this.currentUser = this.authService.getCurrentUser();
      this.initializeRoleContext();
      this.loadDueDates();
      if (!this.isInstructor && !this.isAdmin) {
        this.loadCourses();
      }
      this.cdr.markForCheck();
    });
  }
  private initializeRoleContext(): void {
    if (!this.currentUser) return;

    this.userRole = this.currentUser.currentOrgRole || this.currentUser.role;
    this.isInstructor = this.permissionService.hasRoleLevel('instructor');
    this.isAdmin = this.permissionService.canAccessAdmin();

    // Get organization context for instructors. currentOrgName is only
    // populated on org *switch* — on initial login it's undefined, which
    // surfaced as a literal "undefined • Instructor" tooltip on the
    // Instructor Tools section header. Resolve the name from the user's
    // organizations[] (matched by currentOrgId), and fall back to any
    // legacy organizationName field. Hide the badge entirely if no name
    // can be resolved rather than rendering "undefined".
    if (this.isInstructor) {
      const name = this.resolveCurrentOrgName(this.currentUser);
      this.organizationContext = name
        ? {
            id: this.currentUser.currentOrgId,
            name,
            role: this.currentUser.currentOrgRole
          }
        : null;
    }
  }

  /**
   * Resolve the friendly name for the user's currently-active organization
   * without ever returning the string "undefined". Order of preference:
   *   1. user.currentOrgName (set on org switch)
   *   2. user.organizations[] matched by currentOrgId (set on login)
   *   3. user.organizationName (legacy single-org field)
   *   4. null (caller hides the badge)
   */
  private resolveCurrentOrgName(user: any): string | null {
    if (user?.currentOrgName) return user.currentOrgName;
    const orgId = user?.currentOrgId;
    const orgs: Array<{ id: string; name: string }> | undefined = user?.organizations;
    if (orgId && Array.isArray(orgs)) {
      const matched = orgs.find(o => o?.id === orgId);
      if (matched?.name) return matched.name;
    }
    if (user?.organizationName) return user.organizationName;
    return null;
  }

  loadDueDates(): void {
    this.isLoadingDueDates = true;
    this.dueDatesError = null;

    // Load upcoming due dates (limit to 5 for dashboard)
    this.dueDatesService.getDueDates(5).subscribe({
      next: (response: any) => {
        this.dueDates = (response.data?.dueDates) || [];
        this.isLoadingDueDates = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load due dates:', error);
        this.dueDatesError = 'Failed to load due dates';
        this.dueDates = [];
        this.isLoadingDueDates = false;
        this.cdr.markForCheck();
      }
    });

    // Load summary statistics
    this.dueDatesService.getDueDateSummary().subscribe({
      next: (summary: any) => {
        this.dueDateSummary = summary.data || null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load due date summary:', error);
        this.dueDateSummary = null;
        this.cdr.markForCheck();
      }
    });
  }

  getDueStatusClass(dueStatus: string): string {
    return this.dueDatesService.getDueStatusClass(dueStatus);
  }

  getDueStatusBadgeClass(dueStatus: string): string {
    return this.dueDatesService.getDueStatusBadgeClass(dueStatus);
  }

  getDueStatusText(dueStatus: string, daysUntilDue: number | null): string {
    return this.dueDatesService.getDueStatusText(dueStatus, daysUntilDue);
  }

  formatDate(dateString: string | null): string {
    return this.dueDatesService.formatDate(dateString);
  }
  
  logout(): void {
    // Clear user data from localStorage
    localStorage.removeItem('userData');
    localStorage.removeItem('selectedOrgId'); // Also clear the selected organization
    console.log('User logged out');
    // The router link will navigate to login
  }

  loadCourses(): void {
    this.isLoadingCourses = true;
    this.coursesError = null;

    this.courseService.getUserCourses().subscribe({
      next: (courses: Course[]) => {
        // Limit to first 4 courses for dashboard preview
        this.courses = (courses || []).slice(0, 4);
        this.isLoadingCourses = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load courses:', error);
        this.coursesError = 'Failed to load courses';
        this.courses = [];
        this.isLoadingCourses = false;
        this.cdr.markForCheck();
      }
    });

    // KAN-411 round 4: load certificates so the widget status gates on genuine
    // completion (the certificate is the authoritative gate-aware artifact).
    this.courseService.getUserCertificates().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (certs: Certificate[]) => { this.certificates = certs || []; this.cdr.markForCheck(); },
      error: () => { this.certificates = []; }
    });
  }

  getProgressPercentage(course: Course): number {
    return course.computedProgress || 0;
  }

  // KAN-411 round 4: reuse the SHARED gate-aware resolver (course-list.utils) -
  // same function the My Courses card uses - so module-done-but-gate-blocked
  // courses don't show a false "Completed" here.
  getCourseStatus(course: Course): string {
    const hasCertificate = this.certificates.some(c => c.courseId === course.id);
    return resolveCourseCompletionStatus(course, hasCertificate);
  }

  getStatusBadgeClass(course: Course): string {
    const status = this.getCourseStatus(course);
    switch (status) {
      case 'completed': return 'badge-success';
      case 'in-progress': return 'badge-info';
      case 'overdue': return 'badge-error';
      case 'enrolled': return 'badge-primary';
      default: return 'badge-ghost';
    }
  }

  getStatusText(course: Course): string {
    const status = this.getCourseStatus(course);
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'overdue': return 'Overdue';
      case 'enrolled': return 'Enrolled';
      default: return status;
    }
  }

  navigateToCourse(course: Course): void {
    this.router.navigate(['/courses', course.id]);
  }

  getCourseInitials(courseName: string): string {
    if (!courseName) return '??';
    const cleanName = courseName.replace(/[^a-zA-Z\s]/g, '').trim();
    if (!cleanName) return '??';
    const words = cleanName.split(/\s+/).filter(word => word.length > 0);
    if (words.length === 0) return '??';
    if (words.length === 1) {
      const word = words[0].toUpperCase();
      return word.length >= 2 ? word.substring(0, 2) : word;
    }
    return words[0][0].toUpperCase() + words[words.length - 1][0].toUpperCase();
  }
}
